import api from '@common/api';
import * as storage from '@common/storage';
import { MyProfile } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

interface MyProfileModelProps {
  myProfile?: MyProfile;
  storedSignatureUrl?: string;
}
interface MyProfileModelActions {
  /**
   *
   * @param user_id - cognito_sub id
   * @returns
   */
  fetchMyProfile: (user_id: string) => Promise<MyProfile | null>;
  getStoredSignatureUrl: () => Promise<string | null>;
  setStoredSignatureUrl: (url?: string) => void;
  setMyProfile: (profile: MyProfile | null) => void;
}

export const MyProfileModel = createStore(
  persist<MyProfileModelProps & MyProfileModelActions>(
    (set, get) => ({
      fetchMyProfile: async (user_id: string) => {
        const myProfile = await api.getMyProfile({ user_id });
        let storedSignatureUrl;
        if (myProfile) {
          storedSignatureUrl = (await get().getStoredSignatureUrl()) ?? undefined;
        }
        set({ myProfile: myProfile?.data?.getMyProfile ?? undefined, storedSignatureUrl });
        return myProfile?.data?.getMyProfile ?? null;
      },
      getStoredSignatureUrl: async () => {
        const { myProfile } = get();
        if (!myProfile) return null;
        const signatureS3Key = myProfile?.signature_file?.key;
        if (!signatureS3Key) return null;
        const result = await storage.get(signatureS3Key);
        set({ storedSignatureUrl: result });
        return result;
      },
      setMyProfile: (myProfile) => {
        if (myProfile) {
          set({ myProfile });
        }
      },
      setStoredSignatureUrl: (url?: string) => set({ storedSignatureUrl: url }),
    }),
    {
      getStorage: () => localStorage,
      name: 'myProfile-model',
    },
  ),
);

export function useMyProfileModel(): MyProfileModelProps & MyProfileModelActions;
export function useMyProfileModel<T>(
  selector: (state: MyProfileModelProps & MyProfileModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useMyProfileModel<T>(
  selector?: (state: MyProfileModelProps & MyProfileModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(MyProfileModel, selector!, equals);
}
